import { reactive, ref, getCurrentInstance, onUnmounted, watch, onMounted, computed } from 'vue-demi';

function createGlobalState(factory) {
    var state = reactive(factory());
    return function () { return state; };
}

function useTimeout(interval, startRightNow) {
    if (interval === void 0) { interval = 1000; }
    if (startRightNow === void 0) { startRightNow = true; }
    var ready = ref(false);
    var timer = null;
    function stop() {
        ready.value = false;
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    }
    function start() {
        stop();
        timer = setTimeout(function () {
            ready.value = true;
            timer = null;
        }, interval);
    }
    if (startRightNow)
        start();
    if (getCurrentInstance())
        onUnmounted(stop);
    return { ready: ready, start: start, stop: stop };
}

function useTimeoutFn(cb, interval) {
    var _a = useTimeout(interval), ready = _a.ready, start = _a.start, stop = _a.stop;
    watch(ready, function (maturity) {
        maturity && cb();
    });
    return { ready: ready, start: start, stop: stop };
}

function useAsyncState(promise, defaultState, delay, catchFn) {
    if (delay === void 0) { delay = 0; }
    if (catchFn === void 0) { catchFn = function (e) { }; }
    var state = ref(defaultState);
    var ready = ref(false);
    function run() {
        promise
            .then(function (data) {
            // @ts-ignore
            state.value = data;
            ready.value = true;
        })
            .catch(catchFn);
    }
    if (!delay)
        run();
    else
        useTimeoutFn(run, delay);
    return { state: state, ready: ready };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
var events = ['chargingchange', 'chargingtimechange', 'dischargingtimechange', 'levelchange'];
function useBattery() {
    var charging = ref(false);
    var chargingTime = ref(0);
    var dischargingTime = ref(0);
    var level = ref(1);
    var supported = ref('getBattery' in navigator);
    function updateBatteryInfo() {
        charging.value = this.charging;
        chargingTime.value = this.chargingTime || 0;
        dischargingTime.value = this.dischargingTime || 0;
        level.value = this.level;
    }
    onMounted(function () {
        if (!supported.value)
            return;
        navigator.getBattery().then(function (battery) {
            updateBatteryInfo.call(battery);
            events.forEach(function (evt) {
                battery.addEventListener(evt, updateBatteryInfo);
            });
        });
    });
    onUnmounted(function () {
        if (!supported.value)
            return;
        navigator.getBattery().then(function (battery) {
            events.forEach(function (evt) {
                battery.removeEventListener(evt, updateBatteryInfo);
            });
        });
    });
    return {
        charging: charging,
        chargingTime: chargingTime,
        dischargingTime: dischargingTime,
        level: level,
        supported: supported,
    };
}

function useEventListener(type, listener, options, target) {
    if (target === void 0) { target = window; }
    onMounted(function () {
        target.addEventListener(type, listener, options);
    });
    onUnmounted(function () {
        target.removeEventListener(type, listener, options);
    });
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useBrowserLocation(target) {
    if (target === void 0) { target = ref(document.body); }
    var buildState = function (trigger) {
        var state = history.state, length = history.length;
        var hash = location.hash, host = location.host, hostname = location.hostname, href = location.href, origin = location.origin, pathname = location.pathname, port = location.port, protocol = location.protocol, search = location.search;
        return {
            trigger: trigger,
            state: state,
            length: length,
            hash: hash,
            host: host,
            hostname: hostname,
            href: href,
            origin: origin,
            pathname: pathname,
            port: port,
            protocol: protocol,
            search: search,
        };
    };
    var state = ref(buildState('load'));
    useEventListener('popstate', function () { return state.value = buildState('popstate'); });
    useEventListener('pushstate', function () { return state.value = buildState('pushstate'); });
    useEventListener('replacestate', function () { return state.value = buildState('replacestate'); });
    return state;
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useClipboard() {
    var text = ref('');
    var supported = ref('clipboard' in window.navigator);
    useEventListener('copy', function () {
        window.navigator.clipboard.readText().then(function (value) {
            text.value = value;
        });
    });
    function copy(txt) {
        text.value = txt;
        return window.navigator.clipboard.writeText(txt);
    }
    return {
        text: text,
        copy: copy,
        supported: supported,
    };
}

function useCounter(initialValue) {
    if (initialValue === void 0) { initialValue = 0; }
    var count = ref(initialValue);
    var inc = function (delta) {
        if (delta === void 0) { delta = 1; }
        return (count.value += delta);
    };
    var dec = function (delta) {
        if (delta === void 0) { delta = 1; }
        return (count.value -= delta);
    };
    var get = function () { return count.value; };
    var set = function (val) { return (count.value = val); };
    var reset = function (val) {
        if (val === void 0) { val = initialValue; }
        initialValue = val;
        return set(val);
    };
    return { count: count, inc: inc, dec: dec, get: get, set: set, reset: reset };
}

function useCssVar(prop, refEl) {
    var varRef = ref('');
    var el = computed(function () { return (refEl === null || refEl === void 0 ? void 0 : refEl.value) || document.documentElement; });
    onMounted(function () {
        // @ts-ignore
        varRef.value = getComputedStyle(el.value).getPropertyValue(prop);
    });
    watch(varRef, function (val) {
        // @ts-ignore
        el.value && el.value.style && el.value.style.setProperty(prop, val);
    });
    return varRef;
}

function useDebounceFn(fn, delay) {
    if (delay === void 0) { delay = 200; }
    if (delay <= 0)
        return fn;
    var timer;
    function wrapper() {
        var _this = this;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var exec = function () {
            timer = undefined;
            return fn.apply(_this, args);
        };
        if (timer)
            clearTimeout(timer);
        timer = setTimeout(exec, delay);
    }
    return wrapper;
}

function useDebounce(value, delay) {
    if (delay === void 0) { delay = 200; }
    if (delay <= 0)
        return value;
    var debounced = ref(value.value);
    var updater = useDebounceFn(function () {
        debounced.value = value.value;
    }, delay);
    watch(value, function () { return updater(); });
    return debounced;
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useDeviceLight() {
    var light = ref(null);
    useEventListener('devicelight', function (event) {
        light.value = event.value;
    });
    return light;
}

/**
 * Throttle execution of a function. Especially useful for rate limiting
 * execution of handlers on events like resize and scroll.
 *
 * @param  {Function}  fn             A function to be executed after delay milliseconds. The `this` context and all arguments are passed through, as-is,
 *                                    to `callback` when the throttled-function is executed.
 * @param  {Number}    delay          A zero-or-greater delay in milliseconds. For event callbacks, values around 100 or 250 (or even higher) are most useful.
 *
 * @return {Function}  A new, throttled, function.
 */
function useThrottleFn(fn, delay, trailing) {
    if (delay === void 0) { delay = 200; }
    if (trailing === void 0) { trailing = true; }
    if (delay <= 0)
        return fn;
    var lastExec = 0;
    var timer;
    var lastThis;
    var lastArgs;
    function clear() {
        if (timer) {
            clearTimeout(timer);
            timer = undefined;
        }
    }
    function timeoutCallback() {
        clear();
        fn.apply(lastThis, lastArgs);
    }
    function wrapper() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var elapsed = Date.now() - lastExec;
        clear();
        if (elapsed > delay) {
            lastExec = Date.now();
            fn.apply(this, args);
        }
        else if (trailing) {
            lastArgs = args;
            lastThis = this;
            timer = setTimeout(timeoutCallback, delay);
        }
    }
    return wrapper;
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useDeviceMotion(options) {
    if (options === void 0) { options = { throttleMs: 10 }; }
    var acceleration = ref({ x: null, y: null, z: null });
    var rotationRate = ref({ alpha: null, beta: null, gamma: null });
    var interval = ref(0);
    var accelerationIncludingGravity = ref({
        x: null,
        y: null,
        z: null,
    });
    function onDeviceMotion(event) {
        acceleration.value = event.acceleration;
        accelerationIncludingGravity.value = event.accelerationIncludingGravity;
        rotationRate.value = event.rotationRate;
        interval.value = event.interval;
    }
    var handler = useThrottleFn(onDeviceMotion, options.throttleMs);
    useEventListener('devicemotion', handler, false);
    return {
        acceleration: acceleration,
        accelerationIncludingGravity: accelerationIncludingGravity,
        rotationRate: rotationRate,
        interval: interval,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useDeviceOrientation() {
    var isAbsolute = ref(false);
    var alpha = ref(0);
    var beta = ref(0);
    var gamma = ref(0);
    useEventListener('deviceorientation', function (event) {
        isAbsolute.value = event.absolute;
        alpha.value = event.alpha;
        beta.value = event.beta;
        gamma.value = event.gamma;
    });
    return {
        isAbsolute: isAbsolute,
        alpha: alpha,
        beta: beta,
        gamma: gamma,
    };
}

function useDocumentVisibility() {
    var visibility = ref(document.visibilityState);
    useEventListener('visibilitychange', function () {
        visibility.value = document.visibilityState;
    }, undefined, document);
    return visibility;
}

var isClient = typeof window !== 'undefined';
var isFunction = function (val) { return typeof val === 'function'; };
var isString = function (val) { return typeof val === 'string'; };
var timestamp = function () { return +Date.now(); };
var clamp = function (n, min, max) { return Math.min(max, Math.max(min, n)); };
var noop = function () { };

function useWindowScroll() {
    var x = ref(isClient ? window.pageXOffset : 0);
    var y = ref(isClient ? window.pageYOffset : 0);
    useEventListener('scroll', function () {
        x.value = window.pageXOffset;
        y.value = window.pageYOffset;
    }, {
        capture: false,
        passive: true,
    });
    return { x: x, y: y };
}

function useElementVisibility(element) {
    var _a = useWindowScroll(), x = _a.x, y = _a.y;
    var elementIsVisible = ref(false);
    var testBoundingClientRect = function () {
        if (!element.value) {
            elementIsVisible.value = false;
        }
        else {
            var rect = element.value.getBoundingClientRect();
            elementIsVisible.value = (rect.top <= (window.innerHeight || document.documentElement.clientHeight)
                && rect.left <= (window.innerWidth || document.documentElement.clientWidth)
                && rect.bottom >= 0
                && rect.right >= 0);
        }
    };
    onMounted(testBoundingClientRect);
    watch(x, testBoundingClientRect);
    watch(y, testBoundingClientRect);
    return elementIsVisible;
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useFullscreen(target) {
    if (target === void 0) { target = ref(document.body); }
    var isFullscreen = ref(false);
    function exitFullscreen() {
        if (document.fullscreenElement)
            document.exitFullscreen();
        isFullscreen.value = false;
    }
    function enterFullscreen() {
        exitFullscreen();
        if (target.value) {
            target.value.requestFullscreen().then(function () {
                isFullscreen.value = true;
            });
        }
    }
    return {
        isFullscreen: isFullscreen,
        enterFullscreen: enterFullscreen,
        exitFullscreen: exitFullscreen,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useGeolocation(options) {
    if (options === void 0) { options = {
        enableHighAccuracy: true,
        maximumAge: 30000,
        timeout: 27000,
    }; }
    var locatedAt = ref(null);
    var error = ref(null);
    var coords = ref({
        accuracy: 0,
        latitude: 0,
        longitude: 0,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
    });
    function updatePosition(position) {
        locatedAt.value = position.timestamp;
        coords.value = position.coords;
        error.value = null;
    }
    var watcher;
    onMounted(function () {
        if ('geolocation' in navigator) {
            watcher = window.navigator.geolocation.watchPosition(updatePosition, function (err) {
                error.value = err;
            }, options);
        }
    });
    onUnmounted(function () {
        if (watcher)
            window.navigator.geolocation.clearWatch(watcher);
    });
    return {
        coords: coords,
        locatedAt: locatedAt,
        error: error,
    };
}

var defaultEvents = ['mousemove', 'mousedown', 'resize', 'keydown', 'touchstart', 'wheel'];
var oneMinute = 60e3;
function useIdle(ms, initialState, listeningEvents, throttleDelay) {
    if (ms === void 0) { ms = oneMinute; }
    if (initialState === void 0) { initialState = false; }
    if (listeningEvents === void 0) { listeningEvents = defaultEvents; }
    if (throttleDelay === void 0) { throttleDelay = 50; }
    var idle = ref(initialState);
    var lastActive = ref(timestamp());
    var timeout;
    var set = function (newState) {
        idle.value = newState;
    };
    var onEvent = useThrottleFn(function () {
        idle.value = false;
        clearTimeout(timeout);
        timeout = setTimeout(function () { return idle.value = true; }, ms);
        lastActive.value = timestamp();
    }, throttleDelay);
    for (var _i = 0, listeningEvents_1 = listeningEvents; _i < listeningEvents_1.length; _i++) {
        var eventName = listeningEvents_1[_i];
        useEventListener(eventName, onEvent);
    }
    useEventListener('visibilitychange', function () {
        if (!document.hidden)
            onEvent();
    }, undefined, document);
    onMounted(function () {
        timeout = setTimeout(function () { return set(true); }, ms);
    });
    return { idle: idle, lastActive: lastActive };
}

function useInterval(interval, startRightNow) {
    if (interval === void 0) { interval = 1000; }
    if (startRightNow === void 0) { startRightNow = true; }
    var timer = null;
    var counter = ref(0);
    function stop() {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
    }
    function start() {
        stop();
        timer = setInterval(function () {
            counter.value += 1;
        }, interval);
    }
    if (startRightNow)
        onMounted(start);
    if (getCurrentInstance())
        onUnmounted(stop);
    return { counter: counter, start: start, stop: stop };
}

function useIntervalFn(cb, interval, startRightNow) {
    if (interval === void 0) { interval = 1000; }
    if (startRightNow === void 0) { startRightNow = true; }
    var timer = null;
    function stop() {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
    }
    function start() {
        stop();
        timer = setInterval(cb, interval);
    }
    if (startRightNow)
        start();
    if (getCurrentInstance())
        onUnmounted(stop);
    return { start: start, stop: stop };
}

var Serializers = {
    boolean: {
        read: function (v, d) { return v != null ? v === 'true' : d; },
        write: function (v) { return String(v); },
    },
    object: {
        read: function (v, d) { return v ? JSON.parse(v) : d; },
        write: function (v) { return JSON.stringify(v); },
    },
    number: {
        read: function (v, d) { return v != null ? Number.parseFloat(v) : d; },
        write: function (v) { return String(v); },
    },
    any: {
        read: function (v, d) { return v !== null && v !== void 0 ? v : d; },
        write: function (v) { return String(v); },
    },
    string: {
        read: function (v, d) { return v !== null && v !== void 0 ? v : d; },
        write: function (v) { return String(v); },
    },
};
function useStorage(key, defaultValue, storage) {
    if (storage === void 0) { storage = localStorage; }
    var data = ref(defaultValue);
    var type = defaultValue == null
        ? 'any'
        : typeof defaultValue === 'boolean'
            ? 'boolean'
            : typeof defaultValue === 'string'
                ? 'string'
                : typeof defaultValue === 'object'
                    ? 'object'
                    // @ts-ignore
                    : !Number.isNaN(defaultValue)
                        ? 'number'
                        : 'any';
    function read() {
        try {
            var rawValue = storage.getItem(key);
            if (rawValue === undefined && defaultValue) {
                rawValue = Serializers[type].write(defaultValue);
                storage.setItem(key, rawValue);
            }
            else {
                data.value = Serializers[type].read(rawValue, defaultValue);
            }
        }
        catch (e) {
            console.warn(e);
        }
    }
    read();
    useEventListener('storage', read);
    watch(data, function () {
        try {
            if (data.value == null)
                storage.removeItem(key);
            else
                storage.setItem(key, Serializers[type].write(data.value));
        }
        catch (e) {
            console.warn(e);
        }
    }, { flush: 'sync', deep: true });
    return data;
}

function useLocalStorage(key, defaultValue) {
    // @ts-ignore
    return useStorage(key, defaultValue, localStorage);
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useMediaQuery(query) {
    var mediaQuery;
    // try to fetch initial value (avoid SSR issues)
    if (typeof window !== 'undefined')
        mediaQuery = window.matchMedia(query);
    var matches = ref(mediaQuery ? mediaQuery.matches : false);
    function handler(event) {
        matches.value = event.matches;
    }
    onMounted(function () {
        if (!mediaQuery)
            mediaQuery = window.matchMedia(query);
        matches.value = mediaQuery.matches;
        mediaQuery.addListener(handler);
    });
    onUnmounted(function () {
        mediaQuery.removeListener(handler);
    });
    return matches;
}

function useMouse(options) {
    if (options === void 0) { options = {}; }
    var _a = options.touch, touch = _a === void 0 ? true : _a, _b = options.resetOnTouchEnds, resetOnTouchEnds = _b === void 0 ? true : _b, _c = options.initial, initial = _c === void 0 ? { x: 0, y: 0 } : _c;
    var x = ref(initial.x);
    var y = ref(initial.y);
    useEventListener('mousemove', function (event) {
        x.value = event.pageX;
        y.value = event.pageY;
    });
    if (touch) {
        useEventListener('touchmove', function (event) {
            if (event.touches.length > 0) {
                x.value = event.touches[0].clientX;
                y.value = event.touches[0].clientY;
            }
        });
        if (resetOnTouchEnds) {
            useEventListener('touchend', function () {
                x.value = initial.x;
                y.value = initial.y;
            });
        }
    }
    return {
        x: x,
        y: y,
    };
}

function useMouseInElement(target, options) {
    if (target === void 0) { target = ref(document.body); }
    if (options === void 0) { options = {}; }
    var _a = options.handleOutside, handleOutside = _a === void 0 ? false : _a, _b = options.touch, touch = _b === void 0 ? true : _b;
    var x = ref(0);
    var y = ref(0);
    var elementX = ref(0);
    var elementY = ref(0);
    var elementPositionX = ref(0);
    var elementPositionY = ref(0);
    var elementHeight = ref(0);
    var elementWidth = ref(0);
    var isOutside = ref(false);
    var stop = watch(target, function (el, prevEl, onCleanup) {
        var moveHandler = function (event) {
            var ele = el || document.body;
            var _a = ele.getBoundingClientRect(), left = _a.left, top = _a.top, width = _a.width, height = _a.height;
            if (event instanceof TouchEvent && event.touches.length <= 0)
                return;
            x.value = event instanceof MouseEvent ? event.pageX : event.touches[0].clientX;
            y.value = event instanceof MouseEvent ? event.pageY : event.touches[0].clientY;
            elementPositionX.value = left + window.pageXOffset;
            elementPositionY.value = top + window.pageYOffset;
            elementHeight.value = height;
            elementWidth.value = width;
            var elX = x.value - elementPositionX.value;
            var elY = y.value - elementPositionY.value;
            isOutside.value = elX < 0 || elY < 0 || elX > elementWidth.value || elY > elementHeight.value;
            if (handleOutside || !isOutside.value) {
                elementX.value = elX;
                elementY.value = elY;
            }
        };
        document.addEventListener('mousemove', moveHandler);
        if (touch)
            document.addEventListener('touchmove', moveHandler);
        onCleanup(function () {
            document.removeEventListener('mousemove', moveHandler);
            if (touch)
                document.removeEventListener('touchmove', moveHandler);
        });
    }, { immediate: true });
    return {
        x: x,
        y: y,
        elementX: elementX,
        elementY: elementY,
        elementPositionX: elementPositionX,
        elementPositionY: elementPositionY,
        elementHeight: elementHeight,
        elementWidth: elementWidth,
        isOutside: isOutside,
        stop: stop,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useNetwork() {
    var isOnline = ref(true);
    var saveData = ref(false);
    var offlineAt = ref(undefined);
    var downlink = ref(undefined);
    var downlinkMax = ref(undefined);
    var effectiveType = ref(undefined);
    var type = ref('unknown');
    var navigator = window.navigator;
    var connection = 'connection' in navigator ? navigator.connection : undefined;
    function updateNetworkInformation() {
        isOnline.value = navigator.onLine;
        offlineAt.value = isOnline.value ? undefined : Date.now();
        if (!connection)
            return;
        downlink.value = connection.downlink;
        downlinkMax.value = connection.downlinkMax;
        effectiveType.value = connection.effectiveType;
        saveData.value = connection.saveData;
        type.value = connection.type;
    }
    onMounted(updateNetworkInformation);
    useEventListener('offline', function () {
        isOnline.value = false;
        offlineAt.value = Date.now();
    });
    useEventListener('online', function () {
        isOnline.value = true;
    });
    if (connection)
        useEventListener('change', updateNetworkInformation, false, connection);
    return {
        isOnline: isOnline,
        saveData: saveData,
        offlineAt: offlineAt,
        downlink: downlink,
        downlinkMax: downlinkMax,
        effectiveType: effectiveType,
        type: type,
    };
}

function getTimestamp() {
    return +Date.now();
}
function useNow() {
    var now = ref(getTimestamp());
    var started = false;
    var update = function () {
        requestAnimationFrame(function () {
            now.value = getTimestamp();
            if (started)
                update();
        });
    };
    var start = function () {
        if (!started) {
            started = true;
            update();
        }
    };
    var stop = function () {
        started = false;
    };
    start();
    if (getCurrentInstance())
        onUnmounted(function () { return stop(); });
    return now;
}

function useOnline() {
    var isOnline = useNetwork().isOnline;
    return isOnline;
}

function usePageLeave() {
    var isLeft = ref(false);
    var handler = function (event) {
        event = event || window.event;
        // @ts-ignore
        var from = event.relatedTarget || event.toElement;
        isLeft.value = !from;
    };
    useEventListener('mouseout', handler);
    return isLeft;
}

function useParallax(options) {
    var _a = useDeviceOrientation(), deviceBeta = _a.beta, deviceGamma = _a.gamma;
    var _b = useMouseInElement(options === null || options === void 0 ? void 0 : options.targetElement, { handleOutside: false }), elementX = _b.elementX, elementY = _b.elementY, elementWidth = _b.elementWidth, elementHeight = _b.elementHeight;
    var source = computed(function () {
        if (deviceBeta.value != null && deviceBeta.value != null)
            return 'deviceOrientation';
        return 'mouse';
    });
    var deviceOrientationTiltAdjust = (options === null || options === void 0 ? void 0 : options.deviceOrientationTiltAdjust) || (function (i) { return i; });
    var deviceOrientationRollAdjust = (options === null || options === void 0 ? void 0 : options.deviceOrientationRollAdjust) || (function (i) { return i; });
    var mouseTiltAdjust = (options === null || options === void 0 ? void 0 : options.mouseTiltAdjust) || (function (i) { return i; });
    var mouseRollAdjust = (options === null || options === void 0 ? void 0 : options.mouseRollAdjust) || (function (i) { return i; });
    var roll = computed(function () {
        if (source.value === 'deviceOrientation' && deviceBeta.value != null) {
            var value = -deviceBeta.value / 180;
            return deviceOrientationRollAdjust(value);
        }
        else {
            var value = -(elementY.value - elementHeight.value / 2) / elementHeight.value;
            return mouseRollAdjust(value);
        }
    });
    var tilt = computed(function () {
        if (source.value === 'deviceOrientation' && deviceGamma.value != null) {
            var value = deviceGamma.value / 180;
            return deviceOrientationTiltAdjust(value);
        }
        else {
            var value = (elementX.value - elementWidth.value / 2) / elementWidth.value;
            return mouseTiltAdjust(value);
        }
    });
    return { roll: roll, tilt: tilt, source: source };
}

var noop$1 = function () { };
function usePermission(permissionDesc) {
    var permissionStatus = null;
    var desc = typeof permissionDesc === 'string'
        ? { name: permissionDesc }
        : permissionDesc;
    var state = ref('');
    var onChange = function () {
        if (permissionStatus)
            state.value = permissionStatus.state;
    };
    if ('permissions' in navigator) {
        navigator.permissions
            .query(desc)
            .then(function (status) {
            permissionStatus = status;
            onChange();
            useEventListener('change', onChange, undefined, permissionStatus);
        })
            .catch(noop$1);
    }
    return state;
}

function usePreferredColorScheme() {
    var isLight = useMediaQuery('(prefers-color-scheme: light)');
    var isDark = useMediaQuery('(prefers-color-scheme: dark)');
    return computed(function () {
        if (isDark.value)
            return 'dark';
        if (isLight.value)
            return 'light';
        return 'no-preference';
    });
}

function usePreferredDark() {
    return useMediaQuery('(prefers-color-scheme: dark)');
}

function usePreferredLanguages() {
    var value = ref(navigator.languages);
    useEventListener('languagechange', function () {
        value.value = navigator.languages;
    });
    return value;
}

function useRaf() {
    var now = useNow();
    var startAt = ref(now.value);
    var elapsed = computed(function () {
        return now.value - startAt.value;
    });
    return elapsed;
}

function useRafFn(fn, options) {
    if (options === void 0) { options = {}; }
    var _a = options.startNow, startNow = _a === void 0 ? true : _a;
    var started = false;
    function loop() {
        if (!started)
            return;
        fn();
        requestAnimationFrame(loop);
    }
    function start() {
        if (!started) {
            started = true;
            loop();
        }
    }
    function stop() {
        started = false;
    }
    if (startNow)
        start();
    if (getCurrentInstance())
        onUnmounted(function () { return stop(); });
    return { stop: stop, start: start };
}

function useSessionStorage(key, defaultValue) {
    // @ts-ignore
    return useStorage(key, defaultValue, sessionStorage);
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __spreadArrays() {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
}

function useShare(shareOpts) {
    return __awaiter(this, void 0, void 0, function () {
        var _navigator, granted;
        return __generator(this, function (_a) {
            _navigator = window.navigator;
            if (_navigator && _navigator.share) {
                granted = true;
                if (shareOpts.files && _navigator.canShare)
                    granted = _navigator.canShare({ files: shareOpts.files });
                if (granted)
                    return [2 /*return*/, _navigator.share(shareOpts)];
            }
            return [2 /*return*/];
        });
    });
}

var useStoragePlain = useStorage;

/**
 * Throttle execution of a function. Especially useful for rate limiting
 * execution of handlers on events like resize and scroll.
 *
 * @param  delay  A zero-or-greater delay in milliseconds. For event callbacks, values around 100 or 250 (or even higher) are most useful.
 */
function useThrottle(value, delay) {
    if (delay === void 0) { delay = 200; }
    if (delay <= 0)
        return value;
    if (delay <= 0)
        return value;
    var throttled = ref(value.value);
    var updater = useThrottleFn(function () {
        throttled.value = value.value;
    }, delay);
    watch(value, function () { return updater(); });
    return throttled;
}

var cubicBezier = function (_a) {
    var p0 = _a[0], p1 = _a[1], p2 = _a[2], p3 = _a[3];
    var a = function (a1, a2) { return 1 - 3 * a2 + 3 * a1; };
    var b = function (a1, a2) { return 3 * a2 - 6 * a1; };
    var c = function (a1) { return 3 * a1; };
    var calcBezier = function (t, a1, a2) { return ((a(a1, a2) * t + b(a1, a2)) * t + c(a1)) * t; };
    var getSlope = function (t, a1, a2) { return 3 * a(a1, a2) * t * t + 2 * b(a1, a2) * t + c(a1); };
    var getTforX = function (x) {
        var aGuessT = x;
        for (var i = 0; i < 4; ++i) {
            var currentSlope = getSlope(aGuessT, p0, p2);
            if (currentSlope === 0)
                return aGuessT;
            var currentX = calcBezier(aGuessT, p0, p2) - x;
            aGuessT -= currentX / currentSlope;
        }
        return aGuessT;
    };
    return function (x) { return p0 === p1 && p2 === p3 ? x : calcBezier(getTforX(x), p1, p3); };
};
var presets = {
    linear: [0, 0, 1, 1],
    easeInSine: [0.12, 0, 0.39, 0],
    easeOutSine: [0.61, 1, 0.88, 1],
    easeInQuad: [0.11, 0, 0.5, 0],
    easeOutQuad: [0.5, 1, 0.89, 1],
    easeInCubic: [0.32, 0, 0.67, 0],
    easeOutCubic: [0.33, 1, 0.68, 1],
    easeInOutCubic: [0.65, 0, 0.35, 1],
    easeInQuart: [0.5, 0, 0.75, 0],
    easeOutQuart: [0.25, 1, 0.5, 1],
    easeInOutQuart: [0.76, 0, 0.24, 1],
    easeInQuint: [0.64, 0, 0.78, 0],
    easeOutQuint: [0.22, 1, 0.36, 1],
    easeInOutQuint: [0.83, 0, 0.17, 1],
    easeInExpo: [0.7, 0, 0.84, 0],
    easeOutExpo: [0.16, 1, 0.3, 1],
    easeInOutExpo: [0.87, 0, 0.13, 1],
    easeInCirc: [0.55, 0, 1, 0.45],
    easeOutCirc: [0, 0.55, 0.45, 1],
    easeInOutCirc: [0.85, 0, 0.15, 1],
    easeInBack: [0.36, 0, 0.66, -0.56],
    easeOutBack: [0.34, 1.56, 0.64, 1],
    easeInOutBack: [0.68, -0.6, 0.32, 1.6],
};
function useTransition(baseNumber, options) {
    if (options === void 0) { options = {}; }
    var number = ref(baseNumber.value);
    var normalizedOptions = __assign({ duration: 500, transition: 'linear' }, options);
    var getValue = isFunction(normalizedOptions.transition)
        ? normalizedOptions.transition
        : cubicBezier(isString(normalizedOptions.transition)
            ? presets[normalizedOptions.transition]
            : normalizedOptions.transition);
    var stop = noop;
    watch(baseNumber, function () {
        stop();
        var diff = baseNumber.value - number.value;
        var startValue = number.value;
        var startAt = Date.now();
        var endAt = startAt + normalizedOptions.duration;
        stop = useRafFn(function () {
            var now = Date.now();
            var progress = clamp(1 - ((endAt - now) / normalizedOptions.duration), 0, 1);
            number.value = startValue + (diff * getValue(progress));
            if (progress >= 1)
                stop();
        }).stop;
    }, { immediate: true });
    return number;
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useWebSocket(url) {
    var data = ref(null);
    var state = ref('CONNECTING');
    var ws;
    var close = function close(code, reason) {
        if (!ws)
            return;
        ws.close(code, reason);
    };
    var send = function send(data) {
        if (!ws)
            return;
        ws.send(data);
    };
    onMounted(function () {
        ws = new WebSocket(url);
        ws.onopen = function () {
            state.value = 'OPEN';
        };
        ws.onclose = ws.onerror = function () {
            state.value = 'CLOSED';
        };
        ws.onmessage = function (e) {
            data.value = e.data;
        };
    });
    onUnmounted(function () {
        ws.close();
    });
    return {
        data: data,
        state: state,
        close: close,
        send: send,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useWebWorker(url) {
    var data = ref(null);
    var worker;
    var post = function post(val) {
        if (!worker)
            return;
        worker.postMessage(val);
    };
    var terminate = function terminate() {
        if (!worker)
            return;
        worker.terminate();
    };
    onMounted(function () {
        worker = new Worker(url);
        worker.onmessage = function (e) {
            data.value = e.data;
        };
    });
    onUnmounted(function () {
        worker.terminate();
    });
    return {
        data: data,
        post: post,
        terminate: terminate,
    };
}

/**
 * This function accepts as a parameter a function "userFunc"
 * And as a result returns an anonymous function.
 * This anonymous function, accepts as arguments,
 * the parameters to pass to the function "useArgs" and returns a Promise
 * This function can be used as a wrapper, only inside a Worker
 * because it depends by "postMessage".
 *
 * @param {Function} userFunc {Function} fn the function to run with web worker
 *
 * @returns {Function} returns a function that accepts the parameters
 * to be passed to the "userFunc" function
 */
var jobRunner = function (userFunc) { return function (e) {
    var userFuncArgs = e.data[0];
    // eslint-disable-next-line prefer-spread
    return Promise.resolve(userFunc.apply(undefined, userFuncArgs))
        .then(function (result) {
        // @ts-ignore
        postMessage(['SUCCESS', result]);
    })
        .catch(function (error) {
        // @ts-ignore
        postMessage(['ERROR', error]);
    });
}; };

/**
 *
 * Concatenates the dependencies into a comma separated string.
 * this string will then be passed as an argument to the "importScripts" function
 *
 * @param {Array.<String>}} deps array of string
 * @returns {String} a string composed by the concatenation of the array
 * elements "deps" and "importScripts".
 *
 * @example
 * depsParser(['demo1', 'demo2']) // return importScripts('demo1, demo2')
 */
var depsParser = function (deps) {
    if (deps.length === 0)
        return '';
    var depsString = deps.map(function (dep) { return "" + dep; }).toString();
    return "importScripts('" + depsString + "')";
};

/**
 * Converts the "fn" function into the syntax needed to be executed within a web worker
 *
 * @param {Function} fn the function to run with web worker
 * @param {Array.<String>} deps array of strings, imported into the worker through "importScripts"
 *
 * @returns {String} a blob url, containing the code of "fn" as a string
 *
 * @example
 * createWorkerBlobUrl((a,b) => a+b, [])
 * // return "onmessage=return Promise.resolve((a,b) => a + b)
 * .then(postMessage(['SUCCESS', result]))
 * .catch(postMessage(['ERROR', error])"
 */
var createWorkerBlobUrl = function (fn, deps) {
    var blobCode = depsParser(deps) + "; onmessage=(" + jobRunner + ")(" + fn + ")";
    var blob = new Blob([blobCode], { type: 'text/javascript' });
    var url = URL.createObjectURL(blob);
    return url;
};

/* this implementation is a vue port of https://github.com/alewin/useWorker by Alessio Koci */
var WorkerStatus;
(function (WorkerStatus) {
    WorkerStatus["Pending"] = "PENDING";
    WorkerStatus["Success"] = "SUCCESS";
    WorkerStatus["Runing"] = "RUNNING";
    WorkerStatus["Error"] = "ERROR";
    WorkerStatus["TimeoutExpired"] = "TIMEOUT_EXPIRED";
})(WorkerStatus || (WorkerStatus = {}));
var DEFAULT_OPTIONS = {
    timeout: undefined,
    dependencies: [],
};
/* eslint-disable arrow-parens */
var useWebWorkerFn = function (fn, options) {
    if (options === void 0) { options = DEFAULT_OPTIONS; }
    /* eslint-enable arrow-parens */
    var worker = ref(undefined);
    var workerStatus = ref(WorkerStatus.Pending);
    var promise = ref({});
    var timeoutId = ref(undefined);
    var workerTerminate = function (status) {
        if (status === void 0) { status = WorkerStatus.Pending; }
        if (worker.value && worker.value._url) {
            worker.value.terminate();
            URL.revokeObjectURL(worker.value._url);
            promise.value = {};
            worker.value = undefined;
            window.clearTimeout(timeoutId.value);
            workerStatus.value = status;
        }
    };
    onMounted(function () {
        workerTerminate();
    });
    onUnmounted(function () {
        workerTerminate();
    });
    var generateWorker = function () {
        var _a = options.dependencies, dependencies = _a === void 0 ? DEFAULT_OPTIONS.dependencies || [] : _a, _b = options.timeout, timeout = _b === void 0 ? DEFAULT_OPTIONS.timeout : _b;
        var blobUrl = createWorkerBlobUrl(fn, dependencies);
        var newWorker = new Worker(blobUrl);
        newWorker._url = blobUrl;
        newWorker.onmessage = function (e) {
            var _a = promise.value, _b = _a.resolve, resolve = _b === void 0 ? function () { } : _b, _c = _a.reject, reject = _c === void 0 ? function () { } : _c;
            var _d = e.data, status = _d[0], result = _d[1];
            switch (status) {
                case WorkerStatus.Success:
                    resolve(result);
                    workerTerminate(status);
                    break;
                default:
                    reject(result);
                    workerTerminate(WorkerStatus.Error);
                    break;
            }
        };
        newWorker.onerror = function (e) {
            var _a = promise.value.reject, reject = _a === void 0 ? function () { } : _a;
            reject(e);
            workerTerminate(WorkerStatus.Error);
        };
        if (timeout) {
            timeoutId.value = window.setTimeout(function () {
                workerTerminate(WorkerStatus.TimeoutExpired);
            }, timeout);
        }
        return newWorker;
    };
    var callWorker = function () {
        var fnArgs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            fnArgs[_i] = arguments[_i];
        }
        return new Promise(function (resolve, reject) {
            promise.value = {
                resolve: resolve,
                reject: reject,
            };
            worker.value && worker.value.postMessage([__spreadArrays(fnArgs)]);
            workerStatus.value = (WorkerStatus.Runing);
        });
    };
    var workerFn = function () {
        var fnArgs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            fnArgs[_i] = arguments[_i];
        }
        if (workerStatus.value === WorkerStatus.Runing) {
            /* eslint-disable-next-line no-console */
            console.error('[useWebWorkerFn] You can only run one instance of the worker at a time, if you want to run more than one in parallel, create another instance with the hook useWorker(). Read more: https://github.com/alewin/useWorker');
            /* eslint-disable-next-line prefer-promise-reject-errors */
            return Promise.reject();
        }
        worker.value = generateWorker();
        return callWorker.apply(void 0, fnArgs);
    };
    return {
        workerFn: workerFn,
        workerStatus: workerStatus,
        workerTerminate: workerTerminate,
    };
};

function useWindowSize(initialWidth, initialHeight) {
    if (initialWidth === void 0) { initialWidth = Infinity; }
    if (initialHeight === void 0) { initialHeight = Infinity; }
    var width = ref(isClient ? window.innerWidth : initialWidth);
    var height = ref(isClient ? window.innerHeight : initialHeight);
    if (isClient) {
        useEventListener('resize', function () {
            width.value = window.innerWidth;
            height.value = window.innerHeight;
        });
    }
    return { width: width, height: height };
}

var version = '0.0.34';

export { WorkerStatus, createGlobalState, useAsyncState, useBattery, useBrowserLocation, useClipboard, useCounter, useCssVar, useDebounce, useDebounceFn, useDeviceLight, useDeviceMotion, useDeviceOrientation, useDocumentVisibility, useElementVisibility, useEventListener, useFullscreen, useGeolocation, useIdle, useInterval, useIntervalFn, useLocalStorage, useMediaQuery, useMouse, useMouseInElement, useNetwork, useNow, useOnline, usePageLeave, useParallax, usePermission, usePreferredColorScheme, usePreferredDark, usePreferredLanguages, useRaf, useRafFn, useSessionStorage, useShare, useStorage, useStoragePlain, useThrottle, useThrottleFn, useTimeout, useTimeoutFn, useTransition, useWebSocket, useWebWorker, useWebWorkerFn, useWindowScroll, useWindowSize, version };
